import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  AlertTriangle,
  CalendarClock,
  CloudOff,
  PackageX,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  InlineNotice,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db } from "../lib/db";

interface ServerAlert {
  id: string;
  type:
    | "LOW_STOCK"
    | "EXPIRING_STOCK"
    | "EXPIRED_STOCK"
    | "PAYMENT_REVIEW"
    | string;
  severity:
    | "DANGER"
    | "WARNING"
    | "INFO";
  title: string;
  detail: string;
  action: {
    label: string;
    href: string;
  };
}

export function AlertsPage() {
  const auth = useAuth();
  const products = useLiveQuery(
    () => db.products.toArray(),
    [],
    [],
  );
  const syncIssues = useLiveQuery(
    () =>
      db.syncQueue
        .where("status")
        .equals("FAILED")
        .toArray(),
    [],
    [],
  );
  const cachedAlerts = useLiveQuery(
    async () =>
      ((await db.meta.get("server-alerts"))
        ?.value as
        | ServerAlert[]
        | undefined) ?? [],
    [],
    [],
  );

  const [loading, setLoading] =
    useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (auth.demo || !navigator.onLine) {
      return;
    }

    setLoading(true);
    setError("");

    void api<ServerAlert[]>("/alerts")
      .then(async (alerts) => {
        await db.meta.put({
          key: "server-alerts",
          value: alerts,
        });
      })
      .catch((err) =>
        setError(
          err instanceof Error
            ? err.message
            : "Không thể tải cảnh báo",
        ),
      )
      .finally(() => setLoading(false));
  }, [auth.demo]);

  const localAlerts =
    useMemo<ServerAlert[]>(() => {
      if (!auth.demo) return [];

      return products
        .filter(
          (product) =>
            product.stockQty <=
            product.minStockQty,
        )
        .map((product) => ({
          id: `stock-${product.id}`,
          type: "LOW_STOCK",
          severity:
            product.stockQty <= 0
              ? "DANGER"
              : "WARNING",
          title:
            product.stockQty <= 0
              ? `${product.name} đã hết hàng`
              : `${product.name} sắp hết hàng`,
          detail: `Tồn ${product.stockQty} ${product.baseUnit}`,
          action: {
            label: "Nhập hàng",
            href: "/inventory/receive",
          },
        }));
    }, [auth.demo, products]);

  const alerts = auth.demo
    ? localAlerts
    : cachedAlerts;

  const inventoryAlerts = alerts.filter(
    (alert) =>
      alert.type === "LOW_STOCK",
  );
  const expiryAlerts = alerts.filter(
    (alert) =>
      alert.type ===
        "EXPIRING_STOCK" ||
      alert.type === "EXPIRED_STOCK",
  );
  const paymentAlerts = alerts.filter(
    (alert) =>
      alert.type === "PAYMENT_REVIEW",
  );

  const total =
    alerts.length + syncIssues.length;

  function AlertRows({
    items,
  }: {
    items: ServerAlert[];
  }) {
    return (
      <Card className="alert-list">
        {items.map((alert) => (
          <div
            className="alert-row"
            key={alert.id}
          >
            {alert.type.includes(
              "EXPIR",
            ) ? (
              <CalendarClock
                size={20}
              />
            ) : (
              <AlertTriangle
                size={20}
              />
            )}

            <div>
              <strong>
                {alert.title}
              </strong>
              <small>
                {alert.detail}
              </small>
            </div>

            <Link
              className="btn btn-secondary compact"
              to={alert.action.href}
            >
              {alert.action.label}
            </Link>
          </div>
        ))}
      </Card>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Cần chú ý"
        description="Mỗi cảnh báo đều có hành động tiếp theo; cảnh báo HSD lấy từ tồn theo lô"
      />

      {!navigator.onLine &&
      !auth.demo ? (
        <InlineNotice
          tone="warning"
          title="Đang ngoại tuyến"
        >
          Đang xem cảnh báo đã lưu gần nhất.
          Cảnh báo mới sẽ cập nhật khi có mạng.
        </InlineNotice>
      ) : null}

      {error ? (
        <InlineNotice
          tone="danger"
          title={error}
        />
      ) : null}

      {loading ? (
        <InlineNotice
          tone="info"
          title="Đang cập nhật cảnh báo"
        />
      ) : null}

      {total === 0 ? (
        <EmptyState
          title="Không có việc cần xử lý"
          description="Dữ liệu hiện tại không có cảnh báo quan trọng."
        />
      ) : null}

      {inventoryAlerts.length > 0 ? (
        <section>
          <div className="section-title">
            <div>
              <PackageX size={20} />
              <h2>Tồn kho</h2>
            </div>
            <StatusBadge tone="warning">
              {inventoryAlerts.length} việc
            </StatusBadge>
          </div>
          <AlertRows
            items={inventoryAlerts}
          />
        </section>
      ) : null}

      {expiryAlerts.length > 0 ? (
        <section>
          <div className="section-title">
            <div>
              <CalendarClock size={20} />
              <h2>Hạn sử dụng</h2>
            </div>
            <StatusBadge tone="danger">
              {expiryAlerts.length} việc
            </StatusBadge>
          </div>
          <AlertRows
            items={expiryAlerts}
          />
        </section>
      ) : null}

      {paymentAlerts.length > 0 ? (
        <section>
          <div className="section-title">
            <div>
              <AlertTriangle size={20} />
              <h2>Thanh toán</h2>
            </div>
            <StatusBadge tone="warning">
              {paymentAlerts.length} việc
            </StatusBadge>
          </div>
          <AlertRows
            items={paymentAlerts}
          />
        </section>
      ) : null}

      {syncIssues.length > 0 ? (
        <section>
          <div className="section-title">
            <div>
              <CloudOff size={20} />
              <h2>Dữ liệu</h2>
            </div>
            <StatusBadge tone="danger">
              {syncIssues.length} việc
            </StatusBadge>
          </div>

          <Card className="alert-list">
            {syncIssues.map((item) => (
              <div
                className="alert-row"
                key={item.id}
              >
                <CloudOff size={20} />
                <div>
                  <strong>
                    Giao dịch chưa đồng bộ
                  </strong>
                  <small>
                    {item.error ??
                      "Cần thử đồng bộ lại khi có mạng."}
                  </small>
                </div>
                <Link
                  className="btn btn-secondary compact"
                  to="/more"
                >
                  Kiểm tra
                </Link>
              </div>
            ))}
          </Card>
        </section>
      ) : null}
    </div>
  );
}
